import React from "react";

export function FlatTaxDetails() {
  return (
    <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4 mt-4">
      <h4 className="font-medium text-gray-900 mb-3">Flat Tax Reform — Prediction Task</h4>

      <div className="space-y-4 text-sm text-gray-800">
        <section>
          <h5 className="font-semibold text-gray-900">The Setting</h5>
          <p>
            A large country with a developing economy recently transitioned from a centrally planned system to a market economy. It has been taxing personal income under a progressive system for several years:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-2 ml-2">
            <li><strong>12%</strong> on income up to a modest threshold (roughly the median wage)</li>
            <li><strong>20%</strong> on middle-range income</li>
            <li><strong>30%</strong> on income above a higher threshold</li>
          </ul>
          <p className="mt-2">
            Personal income tax is collected through a mix of withholding and self-reporting:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-2 ml-2">
            <li>
              For most salaried workers, taxes are collected through employer withholding, and compliance is relatively high because income is recorded in payroll systems.
            </li>
            <li>
              For many higher-income individuals and business owners, a substantial share of income comes from sources that are harder to verify through payroll alone (for example, self-employment, small business profits, professional services, commissions, and performance bonuses). These income streams may involve cash transactions, irregular payments, accounting discretion, or transfers to accounts abroad.
            </li>
          </ul>
          <p className="mt-2">
            The tax authority has limited administrative capacity. Some employers and financial institutions provide third-party records, but coverage is uneven outside the formal payroll sector. Audits exist, but they are selective and do not reach most taxpayers in a typical year. Tax authority lacks sufficient staff and adequate record-keeping systems to detect and pursue most tax violations.
          </p>
          <p className="mt-2">
            The economy is growing at a moderate pace. There are no major macroeconomic crises anticipated at the time of the reform.
          </p>
        </section>

        <section>
          <h5 className="font-semibold text-gray-900">The Reform</h5>
          <p>
            Citing the need to simplify the tax system and improve the business climate, the government announces a bold reform: the entire progressive structure will be replaced with a <strong>flat tax of 13%</strong> on all personal income. The change takes effect at the start of the next tax year.
          </p>
          <p className="mt-2">The practical implications are straightforward:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 ml-2">
            <li>
              Taxpayers earning above the middle threshold see their rate <strong>drop from 20–30% to 13%</strong> — a reduction of one-third to more than half.
            </li>
            <li>
              Taxpayers earning below the bottom threshold see their rate <strong>rise slightly from 12% to 13%</strong>.
            </li>
          </ul>
          <p className="mt-2">
            No other major fiscal or economic policies change at the same time.
          </p>
        </section>

        <section>
          <h5 className="font-semibold text-gray-900">Your Task</h5>
          <p className="mb-2">
            <strong>What happened to total personal income tax revenue in the first full year after the reform?</strong>
          </p>
          <div className="space-y-2">
            <p>
              <strong>Option A.</strong> Personal income tax revenue <strong>declined</strong> relative to the prior year.
            </p>
            <p>
              <strong>Option B.</strong> Personal income tax revenue <strong>increased</strong> relative to the prior year.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
}
